import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useUserWatchlists } from '@/hooks/use-user-watchlists'
import { useToast } from '@/hooks/use-toast'
import { watchlistService } from '@/services/watchlist-service'
import { refreshWatchlist } from '@/utils/watchlist-refresh'
import { 
  Users, 
  Calendar, 
  Eye, 
  Trash2, 
  MoreHorizontal,
  Edit2,
  RefreshCw
} from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

const colorVariants = [
  {
    bg: 'bg-blue-50/50 dark:bg-blue-950/20',
    color: 'text-blue-700 dark:text-blue-300',
    borderColor: 'border-blue-200/30 dark:border-blue-800/30'
  },
  {
    bg: 'bg-green-50/50 dark:bg-green-950/20',
    color: 'text-green-700 dark:text-green-300',
    borderColor: 'border-green-200/30 dark:border-green-800/30'
  },
  {
    bg: 'bg-purple-50/50 dark:bg-purple-950/20',
    color: 'text-purple-700 dark:text-purple-300',
    borderColor: 'border-purple-200/30 dark:border-purple-800/30'
  },
  {
    bg: 'bg-pink-50/50 dark:bg-pink-950/20',
    color: 'text-pink-700 dark:text-pink-300',
    borderColor: 'border-pink-200/30 dark:border-pink-800/30'
  },
  {
    bg: 'bg-orange-50/50 dark:bg-orange-950/20',
    color: 'text-orange-700 dark:text-orange-300',
    borderColor: 'border-orange-200/30 dark:border-orange-800/30'
  }
]


export function UserWatchlistsGrid() {
  const navigate = useNavigate()
  const { watchlists, isLoading, error, refetch } = useUserWatchlists()
  const { toast } = useToast()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [refreshingId, setRefreshingId] = useState<string | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    watchlistId: string
    watchlistName: string
  }>({
    open: false,
    watchlistId: '',
    watchlistName: ''
  })
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean
    watchlistId: string
    currentName: string
    newName: string
  }>({
    open: false,
    watchlistId: '',
    currentName: '',
    newName: ''
  })

  const handleViewWatchlist = (watchlistId: string) => {
    navigate(`/instagram/watchlist/${watchlistId}`)
  }

  const openDeleteDialog = (watchlistId: string, watchlistName: string) => {
    setDeleteDialog({
      open: true,
      watchlistId,
      watchlistName
    })
  }

  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      watchlistId: '',
      watchlistName: ''
    })
  }

  const openRenameDialog = (watchlistId: string, currentName: string) => {
    setRenameDialog({
      open: true,
      watchlistId,
      currentName,
      newName: currentName
    })
  }

  const closeRenameDialog = () => {
    setRenameDialog({
      open: false,
      watchlistId: '',
      currentName: '',
      newName: ''
    })
  }

  const handleDeleteWatchlist = async () => {
    const { watchlistId, watchlistName } = deleteDialog
    setDeletingId(watchlistId)
    
    try {
      const result = await watchlistService.deleteWatchlist(watchlistId)
      if (result.success) {
        toast({
          title: "Watchlist deleted",
          description: `"${watchlistName}" has been successfully deleted.`,
          variant: "default",
        })
        await refetch()
        closeDeleteDialog()
      } else {
        toast({
          title: "Error",
          description: "Failed to delete watchlist. Please try again.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error deleting watchlist:', error)
      toast({
        title: "Error",
        description: "Failed to delete watchlist. Please try again.",
        variant: "destructive",
      })
    } finally {
      setDeletingId(null)
    }
  }

  const handleRenameWatchlist = async () => {
    const { watchlistId, newName } = renameDialog
    setRenamingId(watchlistId)
    
    try {
      const result = await watchlistService.renameWatchlist(watchlistId, newName)
      if (result.success) {
        toast({
          title: "Watchlist renamed",
          description: result.message,
          variant: "default",
        })
        await refetch()
        closeRenameDialog()
      } else {
        toast({
          title: "Error",
          description: "Failed to rename watchlist. Please try again.",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error renaming watchlist:', error)
      toast({
        title: "Error",
        description: "Failed to rename watchlist. Please try again.",
        variant: "destructive",
      })
    } finally {
      setRenamingId(null)
    }
  }

  const handleRefreshWatchlist = async (watchlistId: string) => {
    setRefreshingId(watchlistId)
    
    try {
      const result = await refreshWatchlist(watchlistId)
      
      if (result.success) {
        toast({
          title: 'Success',
          description: result.message,
        })
        
        // Refresh the watchlists to show the new one
        refetch()
        
        // Navigate to the new watchlist if it was created
        if (result.newWatchlistId) {
          setTimeout(() => {
            navigate(`/instagram/watchlist/${result.newWatchlistId}`)
          }, 1000)
        }
      } else {
        toast({
          title: 'Refresh Failed',
          description: result.message,
          variant: 'destructive'
        })
      }
      
    } catch (error) {
      console.error('Failed to refresh watchlist:', error)
      toast({
        title: 'Error',
        description: 'Failed to refresh watchlist. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setRefreshingId(null)
    }
  }

  const getColorVariant = (index: number) => {
    return colorVariants[index % colorVariants.length]
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="group">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-5 w-[200px]" />
                  <Skeleton className="h-4 w-full" />
                </div>
                <Skeleton className="h-8 w-8" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-[100px]" />
                  <Skeleton className="h-5 w-[60px]" />
                </div>
                <Skeleton className="h-3 w-[150px]" />
                <Skeleton className="h-8 w-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="p-3 rounded-full bg-destructive/10 w-fit mx-auto mb-4">
            <Users className="h-8 w-8 text-destructive" />
          </div>
          <h3 className="text-lg font-medium mb-2">Failed to load watchlists</h3>
          <p className="text-sm text-muted-foreground mb-6">
            {error}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  // Empty state
  if (watchlists.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="p-3 rounded-full bg-muted/50 w-fit mx-auto mb-4">
            <Users className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">No watchlists yet</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Create your first watchlist to start tracking Instagram accounts and their performance.
          </p>
          <Button onClick={() => navigate('/create-watchlist')}>
            Create Your First Watchlist
          </Button>
        </div>
      </div>
    )
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <>
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {watchlists.map((watchlist, index) => {
            const colorVariant = getColorVariant(index)
            return (
              <div key={watchlist.id}>
                <Card className={`group hover:shadow-md transition-all duration-200 h-full border ${colorVariant.borderColor}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${colorVariant.bg} flex-shrink-0`}>
                        <Users className={`h-4 w-4 ${colorVariant.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-sm font-semibold leading-tight">
                          {watchlist.name}
                        </CardTitle>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openRenameDialog(watchlist.id, watchlist.name)}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                           <DropdownMenuItem 
                            onSelect={(e) => {
                              if (refreshingId === watchlist.id) {
                                e.preventDefault()
                                return
                              }
                              e.preventDefault()
                              handleRefreshWatchlist(watchlist.id)
                            }}
                            disabled={refreshingId === watchlist.id}
                          >
                            <RefreshCw className={`h-4 w-4 mr-2 ${refreshingId === watchlist.id ? 'animate-spin' : ''}`} />
                            {refreshingId === watchlist.id ? 'Refreshing...' : 'Refresh'}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => openDeleteDialog(watchlist.id, watchlist.name)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-xs text-muted-foreground leading-relaxed mb-4 line-clamp-2">
                      {watchlist.description || 'No description provided'}
                    </p>
                    
                    <div className="flex items-center gap-2 mb-4">
                      <Badge variant="outline" className="text-xs">
                        {watchlist.total_accounts} accounts
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        Active
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
                      <Calendar className="h-3 w-3" />
                      <span>Updated {formatDate(watchlist.last_updated)}</span>
                    </div>
                    
                    <Button 
                      size="sm" 
                      className="w-full text-xs"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleViewWatchlist(watchlist.id)
                      }}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View Analytics
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )
          })}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialog.open}
        onOpenChange={closeDeleteDialog}
        title="Delete Watchlist"
        description={`Are you sure you want to delete "${deleteDialog.watchlistName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="destructive"
        onConfirm={handleDeleteWatchlist}
        loading={deletingId === deleteDialog.watchlistId}
      />

      {/* Rename Dialog */}
      <Dialog open={renameDialog.open} onOpenChange={closeRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Watchlist</DialogTitle>
            <DialogDescription>
              Enter a new name for "{renameDialog.currentName}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={renameDialog.newName}
              onChange={(e) => setRenameDialog(prev => ({ ...prev, newName: e.target.value }))}
              placeholder="Enter new watchlist name"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameDialog.newName.trim()) {
                  handleRenameWatchlist()
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRenameDialog}>
              Cancel
            </Button>
            <Button 
              onClick={handleRenameWatchlist}
              disabled={!renameDialog.newName.trim() || renamingId === renameDialog.watchlistId}
            >
              {renamingId === renameDialog.watchlistId ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
} 